import dayjs from 'dayjs';

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

const dateFormats = [
	{
		value: 'dateFormat.type1',
		flatpickrFormat: 'd/m/Y',
		dayjsFormat: 'DD/MM/YYYY'
	},
	{
		value: 'dateFormat.type2',
		flatpickrFormat: 'm/d/Y',
		dayjsFormat: 'MM/DD/YYYY'
	},
	{
		value: 'dateFormat.type3',
		flatpickrFormat: 'Y/m/d',
		dayjsFormat: 'YYYY/MM/DD'
	},
	{
		value: 'dateFormat.type4',
		flatpickrFormat: 'd/M/Y',
		dayjsFormat: 'DD/MMM/YYYY'
	},
	{
		value: 'dateFormat.type5',
		flatpickrFormat: 'M/d/Y',
		dayjsFormat: 'MMM/DD/YYYY'
	},
	{
		value: 'dateFormat.type6',
		flatpickrFormat: 'Y/M/d',
		dayjsFormat: 'YYYY/MMM/DD'
	},
	{
		value: 'dateFormat.type7',
		flatpickrFormat: 'd F Y',
		dayjsFormat: 'DD MMMM YYYY'
	},
	{
		value: 'dateFormat.type8',
		flatpickrFormat: 'F d, Y',
		dayjsFormat: 'MMMM DD, YYYY'
	}
];

const units = {
	metricSystem: {
		km: 'km',
		cm: 'cm',
		kg: 'kg',
		cm3: {
			name: 'cm',
			sup: '3'
		}
	},
	imperialSystem: {
		km: 'mile',
		cm: 'inch',
		kg: 'lb',
		cm3: {
			name: 'inch',
			sup: '3'
		}
	}
};

const useLocale = () => {
	const localization = useSelector((state) => state.user.localization);
	const languageLocalization = useSelector((state) => state.layout.localization);

	const DateFormat = useMemo(
		() => dateFormats.find((format) => format.value === localization?.dateFormat) ?? dateFormats[0],
		[localization?.dateFormat]
	);

	const TimeZone = useMemo(() => {
		const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return localization?.timeZone ?? browserTimezone;
	}, [localization?.timeZone]);

	const UnitSystem = useMemo(() => {
		const unitOfMeasurement = localization?.unitOfMeasurement ?? null;
		return units[unitOfMeasurement] ?? units.metricSystem;
	}, [localization?.unitOfMeasurement]);

	const Currency = useMemo(() => localization?.currency ?? 'SAR', [localization?.currency]);

	const Country = useMemo(() => localization?.countryISO2, [localization?.countryISO2]);

	const LanguageCode = useMemo(() => languageLocalization?.code ?? 'engValue', [languageLocalization?.code]);

	const LangCountryCode = useMemo(() => languageLocalization?.systemCode, [languageLocalization?.systemCode]);

	const MapSettings = useMemo(() => localization?.mapSettings, [localization?.mapSettings]);

	const getLocaleDate = useCallback(
		(date) => dayjs(date).tz(TimeZone).format(DateFormat.dayjsFormat),
		[DateFormat, TimeZone]
	);

	const getDate = useCallback((date) => dayjs(date).utc().format(DateFormat.dayjsFormat), [DateFormat, TimeZone]);

	const getLocaleDateWithTime = useCallback(
		(date) => dayjs(date).tz(TimeZone).format(`${DateFormat.dayjsFormat} HH:mm`),
		[DateFormat, TimeZone]
	);

	const getDateWithTime = useCallback(
		(date) => dayjs(date).utc().format(`${DateFormat.dayjsFormat} HH:mm`),
		[DateFormat, TimeZone]
	);

	const getTime = useCallback((date) => dayjs(date).format('HH:mm'), []);

	const getTimeWithTimeZone = useCallback((date) => dayjs(date).tz(TimeZone).format('HH:mm'), [TimeZone]);

	const getTimeWithAMPM = useCallback((date) => dayjs(date).format('h:mmA'), []);

	const getHour = useCallback((date) => dayjs(date).format('HH'), []);

	const getLocaleDateWithSeconds = useCallback(
		(date) => dayjs(date).tz(TimeZone).format(`${DateFormat.dayjsFormat} HH:mm:ss`),
		[DateFormat, TimeZone]
	);

	return {
		DateFormat,
		TimeZone,
		UnitSystem,
		Currency,
		Country,
		LanguageCode,
		LangCountryCode,
		MapSettings,
		getLocaleDate,
		getDate,
		getLocaleDateWithTime,
		getDateWithTime,
		getTime,
		getTimeWithAMPM,
		getHour,
		getTimeWithTimeZone,
		getLocaleDateWithSeconds
	};
};

export default useLocale;
